import React, { useCallback, useEffect, useState } from "react";
import { useAdminApi } from "./useAdminApi";
import { REGION_LABELS } from "./regionLabels";
import ImageLightbox from "./ImageLightbox";

const PAGE_SIZE = 20;
const UNMATCHED_FILTERS = [
  { key: false, label: "All" },
  { key: true, label: "Missing TheGamesDB + LaunchBox" },
];

function LinksTab({ user, onForbidden }) {
  const apiFetch = useAdminApi(user, onForbidden);
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [nameInput, setNameInput] = useState("");
  const [platformInput, setPlatformInput] = useState("");
  const [name, setName] = useState("");
  const [platform, setPlatform] = useState("");
  const [unmatchedOnly, setUnmatchedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [showNewProductModal, setShowNewProductModal] = useState(false);

  // Debounce the search inputs so it doesn't fire a request on every keystroke —
  // resets back to page 1 since the result set changes.
  useEffect(() => {
    const handle = setTimeout(() => {
      setName(nameInput.trim());
      setPlatform(platformInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(handle);
  }, [nameInput, platformInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (name) params.set("name", name);
      if (platform) params.set("platform", platform);
      if (unmatchedOnly) params.set("unmatched", "true");
      const data = await apiFetch(`/admin/game-links?${params.toString()}`);
      setLinks(data.links);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      if (err.message !== "Forbidden") setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, page, name, platform, unmatchedOnly]);

  useEffect(() => {
    load();
  }, [load]);

  function selectUnmatchedFilter(value) {
    setUnmatchedOnly(value);
    setPage(1);
  }

  async function linkTgdb(gameProductId, tgdbId) {
    await apiFetch(`/admin/game-links/${gameProductId}/tgdb/link`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tgdb_id: tgdbId }),
    });
    load();
  }

  async function unlinkTgdb(gameProductId, entryId) {
    await apiFetch(`/admin/game-links/${gameProductId}/tgdb/unlink`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entry_id: entryId }),
    });
    load();
  }

  async function removeBarcode(gameProductId, barcode) {
    await apiFetch(`/admin/game-links/${gameProductId}/barcodes/remove`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ barcode }),
    });
    load();
  }

  async function addBarcode(gameProductId, barcode) {
    await apiFetch(`/admin/game-links/${gameProductId}/barcodes/add`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ barcode }),
    });
    load();
  }

  async function editIgdbName(entryId, name) {
    await apiFetch(`/admin/igdb-games/${entryId}/name`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    load();
  }

  async function deleteLink(gameProductId) {
    await apiFetch(`/admin/game-links/${gameProductId}`, { method: "DELETE" });
    load();
  }

  async function linkLaunchbox(gameProductId, launchboxId) {
    await apiFetch(`/admin/game-links/${gameProductId}/launchbox/link`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ launchbox_id: launchboxId }),
    });
    load();
  }

  async function unlinkLaunchbox(gameProductId, entryId) {
    await apiFetch(`/admin/game-links/${gameProductId}/launchbox/unlink`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entry_id: entryId }),
    });
    load();
  }

  // Bootstraps a product anchored on an igdb_id+platform_id the caller already knows
  // (see POST /admin/game-links/new's own comment for why both are required and how
  // the duplicate check works) — every OTHER association (barcode, TheGamesDB,
  // LaunchBox) happens afterward through this same card's own link forms once it shows
  // up in the list below. Returns whether a NEW product was created vs. an existing one
  // found, so NewProductForm can tell the admin which happened.
  async function createProduct(igdbId, platformId) {
    const data = await apiFetch("/admin/game-links/new", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ igdb_id: igdbId, platform_id: platformId }),
    });
    load();
    return data.created;
  }

  return (
    <div>
      <p className="kicker">Game identity review</p>
      <h2 className="admin-title admin-title--left">Linked barcode, IGDB &amp; TheGamesDB records</h2>

      <div className="admin-links-search">
        <input
          type="text"
          placeholder="Search by name…"
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          className="admin-tgdb-link-input admin-links-search-input"
        />
        <input
          type="text"
          placeholder="Search by platform…"
          value={platformInput}
          onChange={(e) => setPlatformInput(e.target.value)}
          className="admin-tgdb-link-input admin-links-search-input"
        />
        <div className="admin-linked-filter">
          {UNMATCHED_FILTERS.map((f) => (
            <button
              key={String(f.key)}
              type="button"
              className={`admin-tab admin-linked-filter-btn ${unmatchedOnly === f.key ? "admin-tab-active" : ""}`}
              onClick={() => selectUnmatchedFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button type="button" className="admin-signout-btn" onClick={() => setShowNewProductModal(true)}>
          + New product
        </button>
      </div>

      {loading && <p className="admin-status">Loading…</p>}
      {error && <p className="admin-status admin-error">{error}</p>}

      <div className="admin-links">
        {links && links.map((link) => (
          <LinkCard
            key={link._id}
            link={link}
            onLinkTgdb={linkTgdb}
            onUnlinkTgdb={unlinkTgdb}
            onLinkLaunchbox={linkLaunchbox}
            onUnlinkLaunchbox={unlinkLaunchbox}
            onRemoveBarcode={removeBarcode}
            onAddBarcode={addBarcode}
            onEditIgdbName={editIgdbName}
            onDelete={deleteLink}
            onImageClick={(src, alt) => setLightbox({ src, alt })}
          />
        ))}
        {links && links.length === 0 && <p className="admin-status">No linked games found.</p>}
      </div>

      {links && links.length > 0 && (
        <div className="admin-pagination">
          <button
            type="button"
            className="admin-signout-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            Previous
          </button>
          <span className="admin-pagination-status">
            Page {page} of {totalPages} · {total} total
          </span>
          <button
            type="button"
            className="admin-signout-btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
          >
            Next
          </button>
        </div>
      )}

      <ImageLightbox src={lightbox?.src} alt={lightbox?.alt} onClose={() => setLightbox(null)} />
      {showNewProductModal && (
        <NewProductModal onCreate={createProduct} onClose={() => setShowNewProductModal(false)} />
      )}
    </div>
  );
}

// Popup instead of always-on fields, per direct request — same shape as CoversTab's
// LinkToIgdbForm otherwise: both igdb_id and platform_id required (the button stays
// disabled without both), and the backend's own duplicate check (see POST
// /admin/game-links/new) tells this apart from a genuinely new create, surfaced as a
// plain alert() since the modal closes right after either way — same lightweight,
// no-dedicated-UI approach this tab already uses for delete confirmations.
function NewProductModal({ onCreate, onClose }) {
  const [igdbId, setIgdbId] = useState("");
  const [platformId, setPlatformId] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  async function handleSubmit(e) {
    e.preventDefault();
    const parsedIgdbId = Number(igdbId);
    const parsedPlatformId = Number(platformId);
    if (!Number.isInteger(parsedIgdbId) || parsedIgdbId <= 0 || !Number.isInteger(parsedPlatformId) || parsedPlatformId <= 0) return;

    setCreating(true);
    setError(null);
    try {
      const created = await onCreate(parsedIgdbId, parsedPlatformId);
      if (!created) window.alert("Already registered — showing the existing product.");
      onClose();
    } catch (err) {
      setError(err.message === "Forbidden" ? null : "That igdb_id/platform_id isn't tracked yet");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal admin-card glass" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="admin-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h3 className="admin-modal-title">New product</h3>
        <form className="admin-tgdb-link-form" onSubmit={handleSubmit}>
          <input
            type="number"
            min="1"
            placeholder="igdb_id"
            value={igdbId}
            onChange={(e) => setIgdbId(e.target.value)}
            className="admin-tgdb-link-input"
            autoFocus
          />
          <input
            type="number"
            min="1"
            placeholder="platform_id"
            value={platformId}
            onChange={(e) => setPlatformId(e.target.value)}
            className="admin-tgdb-link-input"
          />
          <button type="submit" className="admin-signout-btn" disabled={creating || !igdbId || !platformId}>
            {creating ? "Creating…" : "Create"}
          </button>
        </form>
        {error && <div className="admin-status admin-error admin-status--inline">{error}</div>}
      </div>
    </div>
  );
}

function LinkCard({
  link, onLinkTgdb, onUnlinkTgdb, onLinkLaunchbox, onUnlinkLaunchbox, onRemoveBarcode, onAddBarcode, onEditIgdbName, onDelete, onImageClick,
}) {
  const [tgdbIdInput, setTgdbIdInput] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState("");
  const [addingBarcode, setAddingBarcode] = useState(false);
  const [barcodeError, setBarcodeError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    const label = link.igdb?.name || "this item";
    if (!window.confirm(`Delete the link for "${label}"? This removes its barcodes and TheGamesDB/IGDB association entirely.`)) {
      return;
    }
    setDeleting(true);
    try {
      await onDelete(link._id);
    } finally {
      setDeleting(false);
    }
  }

  async function handleLink(e) {
    e.preventDefault();
    const tgdbId = Number(tgdbIdInput);
    if (!Number.isInteger(tgdbId) || tgdbId <= 0) return;

    setLinking(true);
    setLinkError(null);
    try {
      await onLinkTgdb(link._id, tgdbId);
      setTgdbIdInput("");
    } catch (err) {
      setLinkError(err.message === "Forbidden" ? null : "Not found on TheGamesDB");
    } finally {
      setLinking(false);
    }
  }

  async function handleAddBarcode(e) {
    e.preventDefault();
    if (!barcodeInput.trim()) return;

    setAddingBarcode(true);
    setBarcodeError(null);
    try {
      await onAddBarcode(link._id, barcodeInput.trim());
      setBarcodeInput("");
    } catch (err) {
      if (err.message === "Forbidden") {
        // handled by the shell
      } else if (err.message === "BARCODE_ALREADY_LINKED") {
        setBarcodeError("Already linked to a different game — remove it there first");
      } else {
        setBarcodeError("Couldn't add that barcode");
      }
    } finally {
      setAddingBarcode(false);
    }
  }

  return (
    <div className="admin-card glass">
      <div className="admin-card-header">
        <div className="admin-card-identity">
          <IgdbIdentity igdb={link.igdb} createdAt={link.created_at} onEditName={onEditIgdbName} />
          <div className="admin-card-barcode">
            <span>Barcodes:</span>{" "}
            {link.barcodes && link.barcodes.length > 0
              ? link.barcodes.map((b) => (
                  <span className="admin-barcode-chip-wrap" key={b}>
                    <code className="admin-barcode-chip">{b}</code>
                    <button
                      type="button"
                      className="admin-tgdb-unlink admin-barcode-remove"
                      onClick={() => {
                        if (window.confirm(`Remove barcode ${b} from this game?`)) onRemoveBarcode(link._id, b);
                      }}
                      aria-label={`Remove barcode ${b}`}
                      title="Remove"
                    >
                      ×
                    </button>
                  </span>
                ))
              : "—"}
            <form className="admin-barcode-add-form" onSubmit={handleAddBarcode}>
              <input
                type="text"
                placeholder="add barcode"
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                className="admin-tgdb-link-input admin-barcode-add-input"
              />
              <button type="submit" className="admin-signout-btn" disabled={addingBarcode || !barcodeInput}>
                {addingBarcode ? "Adding…" : "Add"}
              </button>
            </form>
            {barcodeError && <div className="admin-status admin-error admin-status--inline">{barcodeError}</div>}
          </div>
        </div>
        <div className="admin-card-actions">
          <button
            type="button"
            className="admin-signout-btn admin-delete-btn"
            onClick={handleDelete}
            disabled={deleting}
            title="Delete this game's link — barcodes, IGDB and TheGamesDB association"
          >
            {deleting ? "…" : "Delete"}
          </button>
        </div>
      </div>
      <div className="admin-card-sources">
        <div className="admin-source-panel admin-tgdb-group">
          <div className="admin-source-title">TheGamesDB</div>
          {(!link.tgdbs || link.tgdbs.length === 0) && <div className="admin-source-missing">Not linked</div>}
          {link.tgdbs && link.tgdbs.length > 0 && (
            <div className="admin-tgdb-variants">
              {link.tgdbs.map((tgdb) => (
                <div className="admin-tgdb-variant" key={tgdb._id} title={tgdb.name}>
                  <button
                    className="admin-tgdb-unlink"
                    onClick={() => {
                      if (window.confirm(`Unlink this TheGamesDB cover (${tgdb.name})?`)) onUnlinkTgdb(link._id, tgdb._id);
                    }}
                    aria-label={`Unlink ${tgdb.name}`}
                    title="Unlink"
                  >
                    ×
                  </button>
                  <div className="admin-boxart-sides">
                    {tgdb.boxart_url
                      ? (
                        <img
                          className="admin-boxart admin-boxart--grid admin-boxart-clickable"
                          src={tgdb.boxart_url}
                          alt={`${tgdb.name} front`}
                          onClick={() => onImageClick(tgdb.boxart_url, `${tgdb.name} front`)}
                        />
                      )
                      : <div className="admin-boxart-placeholder">No art</div>}
                    {tgdb.back_boxart_url && (
                      <img
                        className="admin-boxart admin-boxart--grid admin-boxart-clickable"
                        src={tgdb.back_boxart_url}
                        alt={`${tgdb.name} back`}
                        title="Back cover"
                        onClick={() => onImageClick(tgdb.back_boxart_url, `${tgdb.name} back`)}
                      />
                    )}
                  </div>
                  <div className="admin-tgdb-region">{REGION_LABELS[tgdb.region_id] || "Unknown region"}</div>
                </div>
              ))}
            </div>
          )}

          <form className="admin-tgdb-link-form" onSubmit={handleLink}>
            <input
              type="number"
              min="1"
              placeholder="tgdb_id"
              value={tgdbIdInput}
              onChange={(e) => setTgdbIdInput(e.target.value)}
              className="admin-tgdb-link-input"
            />
            <button type="submit" className="admin-signout-btn" disabled={linking || !tgdbIdInput}>
              {linking ? "Linking…" : "Link"}
            </button>
          </form>
          {linkError && <div className="admin-status admin-error admin-status--inline">{linkError}</div>}
        </div>

        <LaunchBoxSourcePanel
          gameProductId={link._id}
          launchboxes={link.launchboxes}
          onLink={onLinkLaunchbox}
          onUnlink={onUnlinkLaunchbox}
          onImageClick={onImageClick}
        />
      </div>
    </div>
  );
}

// The product's real games_launchbox group (see resolveLaunchboxGroup, linking.ts) —
// every LaunchBox item version actually cached/linked for this product, straight from
// `link.launchboxes` (joined server-side in /admin/game-links, same as `tgdbs` above).
// Same link/unlink structure as the TheGamesDB panel above it, on purpose (per direct
// request) — the LaunchBox tab itself is browse/search only now, no linking there.
function LaunchBoxSourcePanel({ gameProductId, launchboxes, onLink, onUnlink, onImageClick }) {
  const [launchboxIdInput, setLaunchboxIdInput] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState(null);

  async function handleLink(e) {
    e.preventDefault();
    const launchboxId = Number(launchboxIdInput);
    if (!Number.isInteger(launchboxId) || launchboxId <= 0) return;

    setLinking(true);
    setLinkError(null);
    try {
      await onLink(gameProductId, launchboxId);
      setLaunchboxIdInput("");
    } catch (err) {
      setLinkError(err.message === "Forbidden" ? null : "That launchbox_id wasn't found");
    } finally {
      setLinking(false);
    }
  }

  const hasLaunchboxes = launchboxes && launchboxes.length > 0;

  return (
    <div className="admin-source-panel admin-launchbox-group">
      <div className="admin-source-title">LaunchBox</div>
      {!hasLaunchboxes && <div className="admin-source-missing">Not linked</div>}
      {hasLaunchboxes && (
        <div className="admin-tgdb-variants">
          {launchboxes.map((entry) => (
            <div className="admin-tgdb-variant" key={entry._id} title={entry.name}>
              <button
                className="admin-tgdb-unlink"
                onClick={() => {
                  if (window.confirm(`Unlink this LaunchBox item (${entry.name})?`)) onUnlink(gameProductId, entry._id);
                }}
                aria-label={`Unlink ${entry.name}`}
                title="Unlink"
              >
                ×
              </button>
              <div className="admin-boxart-sides">
                {entry.cover?.front_url
                  ? (
                    <img
                      className="admin-boxart admin-boxart--grid admin-boxart-clickable"
                      src={entry.cover.front_url}
                      alt={`${entry.name} front (LaunchBox)`}
                      onClick={() => onImageClick(entry.cover.front_url, `${entry.name} front (LaunchBox)`)}
                    />
                  )
                  : <div className="admin-boxart-placeholder">No front</div>}
                {entry.cover?.back_url && (
                  <img
                    className="admin-boxart admin-boxart--grid admin-boxart-clickable"
                    src={entry.cover.back_url}
                    alt={`${entry.name} back (LaunchBox)`}
                    title="Back cover"
                    onClick={() => onImageClick(entry.cover.back_url, `${entry.name} back (LaunchBox)`)}
                  />
                )}
                {entry.label?.front_url && (
                  <img
                    className="admin-boxart admin-boxart--grid admin-boxart-clickable"
                    src={entry.label.front_url}
                    alt={`${entry.name} cart label (LaunchBox)`}
                    title="Cart label"
                    onClick={() => onImageClick(entry.label.front_url, `${entry.name} cart label (LaunchBox)`)}
                  />
                )}
              </div>
              <div className="admin-tgdb-region">{entry.name}</div>
              <div className="admin-tgdb-region">{entry.country_name || "Unknown country"}</div>
              {entry.barcodes && entry.barcodes.length > 0 && (
                <div className="admin-tgdb-region">{entry.barcodes.join(", ")}</div>
              )}
            </div>
          ))}
        </div>
      )}

      <form className="admin-tgdb-link-form" onSubmit={handleLink}>
        <input
          type="number"
          min="1"
          placeholder="launchbox_id"
          value={launchboxIdInput}
          onChange={(e) => setLaunchboxIdInput(e.target.value)}
          className="admin-tgdb-link-input"
        />
        <button type="submit" className="admin-signout-btn" disabled={linking || !launchboxIdInput}>
          {linking ? "Linking…" : "Link"}
        </button>
      </form>
      {linkError && <div className="admin-status admin-error admin-status--inline">{linkError}</div>}
    </div>
  );
}

// Lives inline in the card header now (name/platform/id, above the barcodes) instead
// of its own boxed panel — the header is the "game identity" for the whole card, and
// TheGamesDB/LaunchBox below it are the two comparison boxes.
// `createdAt` is the PRODUCT's own created_at (games_products), not the curated IGDB
// entry's — the two are written at different moments and do drift apart (4 of 178
// products in dev, by up to ~6 days). This one is also what the listing sorts by
// (newest first), so showing it is what makes the row order on screen legible.
function IgdbIdentity({ igdb, createdAt, onEditName }) {
  const [editing, setEditing] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function startEditing() {
    setNameInput(igdb.name);
    setError(null);
    setEditing(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!nameInput.trim() || nameInput.trim() === igdb.name) {
      setEditing(false);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onEditName(igdb._id, nameInput.trim());
      setEditing(false);
    } catch (err) {
      setError(err.message === "Forbidden" ? null : "Couldn't save that name");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="admin-card-identity-igdb">
      {!igdb && <div className="admin-source-missing">Not linked</div>}
      {igdb && !editing && (
        <>
          <div className="admin-source-name">
            {igdb.name}
            <button
              type="button"
              className="admin-barcode-move-btn"
              onClick={startEditing}
              title="Edit name"
              aria-label="Edit name"
            >
              ✎
            </button>
          </div>
          <div className="admin-source-meta">{igdb.platform_name}</div>
          <div className="admin-source-meta">
            <code className="admin-barcode-chip">{igdb.igdb_id} &middot; {igdb.platform_id}</code>
            {createdAt && <span className="admin-source-created">{new Date(createdAt).toLocaleString()}</span>}
          </div>
        </>
      )}
      {igdb && editing && (
        <form className="admin-tgdb-link-form" onSubmit={handleSave}>
          <input
            type="text"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            className="admin-tgdb-link-input"
            autoFocus
          />
          <button type="submit" className="admin-signout-btn" disabled={saving || !nameInput.trim()}>
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" className="admin-signout-btn" onClick={() => setEditing(false)} disabled={saving}>
            Cancel
          </button>
        </form>
      )}
      {error && <div className="admin-status admin-error admin-status--inline">{error}</div>}
    </div>
  );
}

export default LinksTab;
